import { HTMLAttributes, useContext, useState } from 'react'

import { Button, Drawer, Text } from '@hhgtech/hhg-components'
import { Modal } from '@hhgtech/hhg-components/mantine'
import { useIntl } from 'react-intl'
import { shallow } from 'zustand/shallow'

import { callApiNew } from 'api/axios'
import { PATHS } from 'api/paths'
import { getHelloSitesUrl } from 'api/utils/paths'
import { redirectToSSOWithReturnUrl } from 'api/utils/redirects'
import { useCurrentUrl } from 'hooks/useCurrentUrl'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { ReactionType } from 'interfaces/types'
import { AppContext } from 'state/appContext'
import { useArticleStore } from 'state/article'

import { FeedbackForm } from './feedbackForm'
import { StyledArticleFeedback } from './index.styled'

export enum FeedbackStatus {
  // #1: Corresponding with "Inaccurate"
  Inaccurate = 1,
  // #2: Corresponding with "Doesn’t have the information I’m looking for"
  NoRelated,
  // #3: Corresponding with "Other" + message
  Other,
}

export const ArticleFeedback = (props: HTMLAttributes<HTMLDivElement>) => {
  const { formatMessage: f } = useIntl()
  const { currentArticle, isLiked, isDisliked, setIsLiked } = useArticleStore(
    (state) => ({
      currentArticle: state.currentArticle,
      isLiked: state.isLiked,
      isDisliked: state.isDisliked,
      setIsLiked: state.setIsLiked,
    }),
    shallow,
  )
  const {
    state: { isLoggedIn },
  } = useContext(AppContext)
  const { locale } = useRouterExtend()
  const [feedbackOpen, setFeedbackOpen] = useState(false)
  const isMobile = useIsMobile()

  const currentUrl = useCurrentUrl()

  if (isLiked || isDisliked) return null
  const handleReaction = async (type: ReactionType) => {
    if (type === ReactionType.DisLike) {
      setFeedbackOpen(true)
      return
    }
    if (!isLoggedIn) {
      redirectToSSOWithReturnUrl(
        `${getHelloSitesUrl(locale)}${currentArticle?.permalink}?feedback=${
          ReactionType.Like
        }&postId=${currentArticle?.id}`,
        'reaction',
      )
    } else {
      const likeResponse = await callApiNew(
        PATHS.SSO.SSO_FEEDBACK,
        {
          method: 'POST',
          data: {
            post_id: currentArticle?.id,
            feedback: ReactionType.Like,
          },
        },
        'sso',
      )

      if (likeResponse?._data?.total) {
        setIsLiked(true)
      }
    }
  }

  return (
    <>
      <StyledArticleFeedback {...props}>
        <Text size="s2" type="semiBold">
          {f({
            id: 'feedback.title',
            defaultMessage: 'Was this article helpful?',
          })}
        </Text>
        <div className="button-group">
          <Button
            size="sm"
            color="transparent"
            onClick={() => handleReaction(ReactionType.Like)}
            data-event-category="FeedbackIcon"
            data-event-action="Yes"
            data-event-label={currentUrl}
            className="pointer-event-child-none"
          >
            <svg width="49" height="48" viewBox="0 0 49 48" fill="none">
              <rect x="0.5" width="48" height="48" rx="4" fill="#00CB8A" />
              <path
                d="M32.4803 17.9902C32.2205 17.9979 31.9739 18.1065 31.7928 18.2929L21.4998 28.5859L18.2069 25.2929C18.1147 25.1969 18.0044 25.1203 17.8822 25.0675C17.7601 25.0147 17.6287 24.9869 17.4956 24.9855C17.3626 24.9842 17.2306 25.0094 17.1074 25.0597C16.9843 25.1099 16.8724 25.1843 16.7783 25.2784C16.6842 25.3725 16.6098 25.4844 16.5595 25.6075C16.5093 25.7307 16.484 25.8627 16.4854 25.9957C16.4868 26.1288 16.5146 26.2602 16.5674 26.3823C16.6202 26.5045 16.6968 26.6148 16.7928 26.707L20.7928 30.707C20.9804 30.8944 21.2347 30.9998 21.4998 30.9998C21.765 30.9998 22.0193 30.8944 22.2069 30.707L33.2069 19.707C33.3514 19.5665 33.4501 19.3857 33.49 19.1882C33.53 18.9906 33.5093 18.7857 33.4308 18.6001C33.3523 18.4145 33.2195 18.2569 33.0499 18.1481C32.8803 18.0393 32.6817 17.9842 32.4803 17.9902Z"
                fill="white"
              />
            </svg>
          </Button>
          <Button
            size="sm"
            color="transparent"
            onClick={() => handleReaction(ReactionType.DisLike)}
            data-event-category="FeedbackIcon"
            data-event-action="No"
            data-event-label={currentUrl}
            className="pointer-event-child-none"
          >
            <svg width="49" height="48" viewBox="0 0 49 48" fill="none">
              <rect x="0.5" width="48" height="48" rx="4" fill="#FF6F4D" />
              <path
                d="M18.4901 16.9902C18.2911 16.9902 18.0967 17.0496 17.9317 17.1607C17.7667 17.2718 17.6386 17.4296 17.5637 17.614C17.4889 17.7983 17.4707 18.0007 17.5115 18.1954C17.5523 18.3901 17.6502 18.5682 17.7928 18.707L23.0858 23.9999L17.7928 29.2929C17.6968 29.3851 17.6202 29.4954 17.5674 29.6176C17.5146 29.7397 17.4868 29.8711 17.4854 30.0042C17.484 30.1372 17.5093 30.2692 17.5595 30.3924C17.6098 30.5155 17.6842 30.6274 17.7783 30.7215C17.8724 30.8156 17.9843 30.89 18.1074 30.9402C18.2306 30.9905 18.3626 31.0157 18.4956 31.0144C18.6287 31.013 18.7601 30.9851 18.8822 30.9324C19.0044 30.8796 19.1147 30.803 19.2069 30.707L24.4998 25.414L29.7928 30.707C29.885 30.803 29.9953 30.8796 30.1175 30.9324C30.2396 30.9851 30.371 31.013 30.5041 31.0144C30.6371 31.0157 30.7691 30.9905 30.8922 30.9402C31.0154 30.89 31.1273 30.8156 31.2214 30.7215C31.3155 30.6274 31.3899 30.5155 31.4401 30.3924C31.4904 30.2692 31.5156 30.1372 31.5143 30.0042C31.5129 29.8711 31.485 29.7397 31.4323 29.6176C31.3795 29.4954 31.3028 29.3851 31.2069 29.2929L25.9139 23.9999L31.2069 18.707C31.3514 18.5665 31.4501 18.3857 31.49 18.1882C31.53 17.9906 31.5093 17.7857 31.4308 17.6001C31.3523 17.4145 31.2195 17.2569 31.0499 17.1481C30.8803 17.0393 30.6817 16.9842 30.4803 16.9902C30.2205 16.9979 29.9739 17.1065 29.7928 17.2929L24.4998 22.5859L19.2069 17.2929C19.1137 17.1971 19.0022 17.121 18.8791 17.069C18.756 17.017 18.6237 16.9902 18.4901 16.9902Z"
                fill="white"
              />
            </svg>
          </Button>
        </div>
      </StyledArticleFeedback>
      {isMobile ? (
        <Drawer
          title={f({
            id: 'feedback.modal.title',
            defaultMessage: 'How can we improve it?',
          })}
          open={feedbackOpen}
          onClose={() => setFeedbackOpen(false)}
          placement="bottom"
        >
          <FeedbackForm
            key={Number(feedbackOpen)}
            onSubmit={() => setFeedbackOpen(false)}
          ></FeedbackForm>
        </Drawer>
      ) : (
        <Modal
          size={500}
          opened={feedbackOpen}
          onClose={() => setFeedbackOpen(false)}
          centered
          title={
            <Text size="s2" type="semiBold">
              {f({
                id: 'feedback.modal.title',
                defaultMessage: 'How can we improve it?',
              })}
            </Text>
          }
        >
          <FeedbackForm
            key={Number(feedbackOpen)}
            onSubmit={() => setFeedbackOpen(false)}
          ></FeedbackForm>
        </Modal>
      )}
    </>
  )
}
